import React from "react";
import type { Monaco } from "@monaco-editor/react";

const Editor = React.lazy(async () => {
  const [{ default: MonacoEditor, loader }, monaco] = await Promise.all([
    import("@monaco-editor/react"),
    import("monaco-editor"),
  ]);

  // Supplying the bundled Monaco instance prevents @monaco-editor/loader from
  // falling back to its default CDN. This remains inside the lazy factory so
  // Monaco and its React wrapper are downloaded only when the editor renders.
  loader.config({ monaco });

  window.MonacoEnvironment = {
    getWorker: (_moduleId: string, label: string) => {
      switch (label) {
        case "json":
          return new Worker(
            new URL(
              "monaco-editor/esm/vs/language/json/json.worker",
              import.meta.url,
            ),
          );
        case "css":
        case "scss":
        case "less":
          return new Worker(
            new URL(
              "monaco-editor/esm/vs/language/css/css.worker",
              import.meta.url,
            ),
          );
        case "html":
        case "handlebars":
        case "razor":
          return new Worker(
            new URL(
              "monaco-editor/esm/vs/language/html/html.worker",
              import.meta.url,
            ),
          );
        case "typescript":
        case "javascript":
          return new Worker(
            new URL(
              "monaco-editor/esm/vs/language/typescript/ts.worker",
              import.meta.url,
            ),
          );
        default:
          return new Worker(
            new URL(
              "monaco-editor/esm/vs/editor/editor.worker",
              import.meta.url,
            ),
          );
      }
    },
  };

  return { default: MonacoEditor };
});

export interface SyntaxEditorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  language?: string;
  style?: React.CSSProperties;
}

/**
 * Presents a Monaco editor with minimal configuration for general syntax editing.
 * @param props - Editor value, change callback, and optional styling overrides.
 * @returns A fully-sized Monaco editor ready for arbitrary language content.
 */
export default function SyntaxEditor(props: SyntaxEditorProps) {
  // Use Monaco's built-in HTML language; no custom registration/theme required
  /**
   * Placeholder hook for pre-mount Monaco setup when consumers need to extend behavior.
   * @param _monaco - Monaco namespace passed by the editor lifecycle.
   */
  const handleEditorWillMount = (_monaco: Monaco) => {};
  /**
   * Placeholder hook for post-mount customization while keeping the API surface consistent.
   * @param _editor - Monaco editor instance.
   * @param _monaco - Monaco namespace.
   */
  const handleEditorDidMount = (_editor: any, _monaco: Monaco) => {};

  return (
   <div style={{ height: "500px", border: "1px solid #ccc", ...props.style }}>
    <React.Suspense fallback={null}>
      <Editor
        width="100%"
        height="100%"
        className={`fbx-full-height ${props.className ?? ""}`}
        language={props.language || "html"}
        theme="vs"
        value={props.value}
        onChange={(newValue: string | undefined) => props.onChange(newValue || "")}
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          automaticLayout: true,
        }}
        beforeMount={handleEditorWillMount}
        onMount={handleEditorDidMount}
      />
    </React.Suspense>
    </div>
  );
}
